import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTheme, ThemeProvider } from '../../theme/ThemeProvider';
import useAuth from '../../hooks/useAuth';
import logger from '../../lib/logger';
import CaseFlowLogo from './CaseFlowLogo';
import LanguageSwitcher from './LanguageSwitcher';
import { FeedbackButtons } from '../feedback/FeedbackButtons';
import { useTranslation } from '../../i18n/LanguageProvider';

interface AppShellProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  labelKey: string;
  icon: string;
  allowedRoles: string[];
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { path: '/dashboard', labelKey: 'navigation.home', icon: '◈', allowedRoles: ['admin', 'technician', 'agent', 'viewer'] },
  { path: '/customers', labelKey: 'navigation.customers', icon: '●', allowedRoles: ['admin', 'technician', 'agent', 'viewer'] },
  { path: '/workspace', labelKey: 'navigation.workspace', icon: '◐', allowedRoles: ['admin', 'technician', 'agent'] },
  { path: '/ai', labelKey: 'navigation.ai', icon: '✦', allowedRoles: ['admin', 'agent', 'viewer'] },
  { path: '/trust', labelKey: 'navigation.trust', icon: '⚑', allowedRoles: ['admin', 'technician', 'agent', 'viewer'] },
  { path: '/system-health', labelKey: 'navigation.systemHealth', icon: '⚙', allowedRoles: ['admin'], adminOnly: true },
  { path: '/admin/tenant', labelKey: 'navigation.adminTenant', icon: '⚒', allowedRoles: ['admin'], adminOnly: true },
  { path: '/enterprise', labelKey: 'navigation.enterprise', icon: '◆', allowedRoles: ['admin'], adminOnly: true },
];

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const { theme, toggleTheme } = useTheme();
  const { user, profile, organization, role, isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const { t } = useTranslation();

  // Kontrollera om användaren har tillgång till en nav-item
  const canAccess = (item: NavItem): boolean => {
    if (!role) return false;
    return item.allowedRoles.includes(role);
  };

  // Hantera utloggning
  const handleLogout = async () => {
    try {
      logger.audit('Användare initierar utloggning från AppShell', {});
      await signOut();
      navigate('/login');
    } catch (error) {
      logger.error('Fel vid utloggning från AppShell', { error: String(error) });
    }
  };

  // Beräkna roll-badge färg
  const getRoleBadgeColor = (): string => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'technician': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'agent': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'viewer': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
      <div className="flex min-h-screen">
        {/* Sidebar - fixed flex column layout */}
        <aside className={`${isSidebarOpen ? 'w-72' : 'w-20'} border-r transition-all duration-300 flex flex-col relative ${
          theme === 'dark' 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-200'
        }`}>
          {/* Navigation section - takes remaining space */}
          <div className="p-5 flex-1 overflow-y-auto">
            {/* Logo/Branding */}
            <div className="mb-8 flex items-center gap-3">
              <CaseFlowLogo
                className="flex-shrink-0"
                heightClass="h-12"
              />
              {isSidebarOpen && (
                <div>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('common.appDescription') || 'AI-drivet kundoperationssystem'}
                  </p>
                </div>
              )}
            </div>

            {/* Navigation */}
            <nav className="space-y-2 text-sm">
              {NAV_ITEMS.map((item) => {
                if (!canAccess(item)) return null;
                
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) => 
                      `flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition ${
                        isActive 
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-white' 
                          : `${theme === 'dark' ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`
                      } ${!isSidebarOpen ? 'justify-center' : ''}`
                    }
                    title={t(item.labelKey)}
                  >
                    <span className="text-lg flex-shrink-0">{item.icon}</span>
                    {isSidebarOpen && <span className="truncate">{t(item.labelKey)}</span>}
                  </NavLink>
                );
              })}
            </nav>
          </div>

          {/* User Info at Bottom - sticky footer */}
          {isSidebarOpen && (
            <div className={`p-4 border-t ${
              theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm flex-shrink-0">
                  {profile?.full_name?.[0] || user?.email?.[0] || '?'}
                </div>
                <div className="min-w-0 flex-1 overflow-hidden">
                  <p className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {profile?.full_name || user?.email || 'Användare'}
                  </p>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor()}`}>
                    {role || 'viewer'}
                  </span>
                </div>
              </div>
              
              {organization && (
                <p className={`text-xs truncate mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {organization.name}
                </p>
              )}

              <button
                onClick={() => setShowLogoutConfirm(true)}
                className={`w-full py-2 px-3 rounded-lg text-sm transition ${
                  theme === 'dark' 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.logout') || 'Logga ut'}
              </button>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          {/* Header */}
          <header className={`sticky top-0 z-10 border-b px-5 py-3 flex items-center justify-between ${
            theme === 'dark' 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-gray-200'
          }`}>
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className={`p-2 rounded-lg transition ${
                theme === 'dark' 
                  ? 'hover:bg-gray-700 text-gray-300' 
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
              aria-label={isSidebarOpen ? (t('common.hideSidebar') || 'Dölj sidofält') : (t('common.showSidebar') || 'Visa sidofält')}
            >
              ☰
            </button>

            <div className="flex items-center gap-3">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition ${
                  theme === 'dark' 
                    ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
                aria-label={theme === 'dark' ? (t('common.lightMode') || 'Byt till ljust läge') : (t('common.darkMode') || 'Byt till mörkt läge')}
              >
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>

              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* Feedback Buttons */}
              <FeedbackButtons />

              {/* User Menu (compact) */}
              <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                {!isSidebarOpen && (
                  <span className="hidden md:inline">
                    {profile?.full_name || user?.email || 'Användare'}
                  </span>
                )}
              </div>
            </div>
          </header>

          {/* Page Content */}
          <div className="p-5 lg:p-8">
            {children}
          </div>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-5 backdrop-blur-sm">
          <div className={`w-full max-w-md rounded-3xl border p-6 shadow-2xl ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <h3 className={`text-lg font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {t('common.confirmLogout') || 'Bekräfta utloggning'}
            </h3>
            <p className={`text-sm mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              {t('common.logoutMessage') || 'Är du säker på att du vill logga ut? Alla öppna kunduppgifter kommer att stängas.'}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  theme === 'dark' 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.cancel') || 'Avbryt'}
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition"
              >
                {t('common.logout') || 'Logga ut'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppShell;
